import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { differenceInDays, subDays, subHours, subMonths, subWeeks, subYears } from 'date-fns';
import { V2QsOnsetProps, V2QsOnsetPreset, V2QsOnsetPresetType } from './types';

/**
 * 날짜/시간 문자열 파싱
 */
function parseDateTimeString(value?: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };

  const datePart = value.substring(0, 10);
  const timePart = value.substring(11, 16);

  return {
    date: datePart.length === 10 ? datePart : '',
    time: timePart.length >= 5 ? timePart : '',
  };
}

/**
 * Date 객체를 YYYY-MM-DD HH:mm 형식으로 포맷
 */
function formatDateTime(date: Date, includeTime: boolean): { date: string; time: string } {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return {
    date: `${year}-${month}-${day}`,
    time: includeTime ? `${hours}:${minutes}` : '',
  };
}

/**
 * 기본 프리셋 생성
 */
function createDefaultPresets(
  types: V2QsOnsetPresetType[],
  labels?: Record<string, string>
): V2QsOnsetPreset[] {
  const presetMap: Record<V2QsOnsetPresetType, V2QsOnsetPreset> = {
    hours: {
      key: 'hours',
      label: labels?.hours || 'In hours',
      getDate: () => subHours(new Date(), 3),
      includeTime: true,
    },
    days: {
      key: 'days',
      label: labels?.days || 'In days',
      getDate: () => subDays(new Date(), 4),
      includeTime: false,
    },
    weeks: {
      key: 'weeks',
      label: labels?.weeks || 'In weeks',
      getDate: () => subWeeks(new Date(), 3),
      includeTime: false,
    },
    months: {
      key: 'months',
      label: labels?.months || 'In months',
      getDate: () => subMonths(new Date(), 4),
      includeTime: false,
    },
    years: {
      key: 'years',
      label: labels?.years || 'In years',
      getDate: () => subYears(new Date(), 2),
      includeTime: false,
    },
  };

  return types.map((type) => presetMap[type]);
}

/**
 * V2QsOnset 컴포넌트
 *
 * 증상 시작 시점 선택을 위한 컴포넌트
 * 빠른 선택 프리셋 + 날짜/시간 미세 조정 지원
 *
 * @example
 * ```tsx
 * <V2QsOnset
 *   value={onsetDate}
 *   onChange={setOnsetDate}
 *   presetLabels={{
 *     hours: '몇 시간 전',
 *     days: '며칠 전',
 *     weeks: '몇 주 전',
 *     months: '몇 달 전',
 *     years: '몇 년 전',
 *   }}
 * />
 * ```
 */
function V2QsOnset({
  value,
  onChange,
  presets: customPresets,
  defaultPresetTypes = ['hours', 'days', 'weeks', 'months', 'years'],
  presetLabels,
  timeInputThresholdDays = 2,
  error = false,
  errorMessage,
  disabled = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  adjustmentHint = 'Please finalize by adjusting the date and time',
  maxDate,
}: V2QsOnsetProps) {
  const { date: initDate, time: initTime } = parseDateTimeString(value);

  const [dateValue, setDateValue] = useState(initDate);
  const [timeValue, setTimeValue] = useState(initTime);

  const hasValue = !!dateValue;

  // 프리셋 목록
  const presets = useMemo(
    () => customPresets || createDefaultPresets(defaultPresetTypes, presetLabels),
    [customPresets, defaultPresetTypes, presetLabels]
  );

  // 외부 value 변경 시 동기화
  useEffect(() => {
    const { date, time } = parseDateTimeString(value);
    if (date !== dateValue) setDateValue(date);
    if (time !== timeValue) setTimeValue(time);
  }, [value]);

  // 값 변경 시 onChange 호출
  useEffect(() => {
    if (dateValue) {
      const finalValue = timeValue ? `${dateValue} ${timeValue}` : dateValue;
      if (finalValue !== value) {
        onChange?.(finalValue);
      }
    }
  }, [dateValue, timeValue]);

  // 오늘과의 날짜 차이 계산
  const daysDiff = useMemo(() => {
    if (!dateValue) return Infinity;
    const today = new Date();
    const selectedDate = new Date(dateValue);
    return differenceInDays(today, selectedDate);
  }, [dateValue]);

  // 시간 입력 필요 여부
  const needTimeInput = daysDiff < timeInputThresholdDays;

  // 프리셋 클릭 핸들러
  const handlePresetClick = useCallback(
    (preset: V2QsOnsetPreset) => {
      if (disabled) return;

      const date = preset.getDate();
      const { date: dateStr, time: timeStr } = formatDateTime(date, !!preset.includeTime);

      setDateValue(dateStr);
      setTimeValue(timeStr);
    },
    [disabled]
  );

  // 날짜 변경 핸들러
  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDateValue(e.target.value);
    },
    []
  );

  // 시간 변경 핸들러
  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTimeValue(e.target.value);
    },
    []
  );

  const hasError = error;
  const maxDateStr = maxDate
    ? maxDate.toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Wrapper 클래스
  const wrapperClasses = classNames(
    'flex flex-col gap-3',
    {
      'w-full': fullWidth,
    },
    className
  );

  // 프리셋 버튼 클래스
  const presetButtonClasses = (isDisabled: boolean) =>
    classNames(
      'px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
      'border-[1.5px]',
      {
        'border-[#E6E9F5] text-[#222638] bg-white hover:border-[#4B7BF5] hover:text-[#4B7BF5]':
          !isDisabled,
        'border-[#E6E9F5] text-[#AFB2C2] bg-[#F2F4FC] cursor-not-allowed': isDisabled,
      }
    );

  // Input 클래스
  const inputClasses = classNames(
    'px-4 py-3 rounded-[10px] transition-all duration-200',
    'border-[1.5px] text-[15px] font-medium',
    'outline-none',
    {
      'border-[#E6E9F5] focus:border-[#4B7BF5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'border-[#E6E9F5] bg-[#F2F4FC] text-[#AFB2C2] cursor-not-allowed': disabled,
    }
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 조정 힌트 클래스
  const adjustmentHintClasses = 'text-sm text-[#AFB2C2]';

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      <div className={wrapperClasses}>
        {/* Preset Buttons */}
        <div
          className={classNames(
            'flex gap-2 flex-wrap transition-all duration-200 overflow-hidden',
            hasValue ? 'max-h-0 opacity-0' : 'max-h-32 opacity-100'
          )}
        >
          {presets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={presetButtonClasses(disabled)}
              onClick={() => handlePresetClick(preset)}
              disabled={disabled}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Date/Time Inputs */}
        <div
          className={classNames(
            'flex flex-col gap-3 transition-all duration-200 overflow-hidden',
            !hasValue ? 'max-h-0 opacity-0' : 'max-h-32 opacity-100'
          )}
        >
          {/* Adjustment Hint */}
          {hasValue && adjustmentHint && (
            <span className={adjustmentHintClasses}>{adjustmentHint}</span>
          )}

          <div className="flex gap-3 flex-wrap">
            {/* Date Input */}
            <input
              type="date"
              value={dateValue}
              onChange={handleDateChange}
              max={maxDateStr}
              disabled={disabled}
              className={inputClasses}
            />

            {/* Time Input (conditional) */}
            {needTimeInput && (
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                disabled={disabled}
                className={inputClasses}
              />
            )}
          </div>
        </div>
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsOnset);
